import { type FC, useEffect, useState } from "react"

import { type CoursePageInputType, coursePageInputTypes } from "@forento/core/models/course"
import { useFile } from "@forento/ui/hooks/useFile"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"

import Attachments from "./Attachments"
import Links from "./Links"
import { ButtonsGroup, ButtonsRow } from "./styles"

type Props = {
	page: {
		id: number
		title: string
		text: string | null
		image: { filePath: string; contain: boolean } | null
		inputType: CoursePageInputType | null
		attachments: { id: number; fileType: string; label: string; url: string }[]
		links: { id: number; label: string; url: string }[]
	}
	isSaving: boolean
	isDeleting: boolean
	onClose(): void
	onSavePage(data: {
		title: string
		text: string | null
		imageDataUrl: string | null | undefined
		containImage: boolean
		inputType: CoursePageInputType
	}): void
	onDeletePage(): void
}
const InputContent: FC<Props> = ({ page, isSaving, isDeleting, onClose, onSavePage, onDeletePage }) => {
	const [title, setTitle] = useState(page.title)
	const text = useRichTextArea({ label: "Text", initialValue: page.text, extendedFormatting: true })
	const image = useFile(page.id, page.image?.filePath ?? null)
	const [containImage, setContainImage] = useState(page.image?.contain ?? false)
	const [inputType, setInputType] = useState<CoursePageInputType>(page.inputType ?? "single-line")

	useEffect(() => {
		setTitle(page.title)
		setContainImage(page.image?.contain ?? false)
		setInputType(page.inputType ?? "single-line")
	}, [page])

	return (
		<>
			<InputField label="Title" value={title} onChange={setTitle} />
			{text.element}
			<Dropdown
				items={coursePageInputTypes.map(inputType => ({
					id: inputType,
					title: inputType === "single-line" ? "Single line" : "Multiple lines",
				}))}
				selectedItemId={inputType}
				onChange={value => setInputType(value as CoursePageInputType)}
			/>
			<ImageUploader label="Image" recommendedSize="thumbnail" {...image.imageUploader} />
			{image.filePath !== null && (
				<Dropdown
					items={[
						{ id: "cover", title: "Show as banner" },
						{ id: "contain", title: "Show full image" },
					]}
					selectedItemId={containImage ? "contain" : "cover"}
					onChange={id => setContainImage(id === "contain")}
				/>
			)}
			<Attachments pageId={page.id} attachments={page.attachments} />
			<Links pageId={page.id} links={page.links} />
			<ButtonsRow>
				<ButtonsGroup>
					<Button variant="secondary" onClick={onClose} isDisabled={isSaving || isDeleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						isDisabled={title.trim().length === 0 || isDeleting}
						isLoading={isSaving}
						onClick={async () =>
							onSavePage({
								title,
								text: await text.exportEditorState(),
								imageDataUrl: image.isModified ? await image.exportToDataUrl() : undefined,
								containImage,
								inputType,
							})
						}
					>
						Save
					</Button>
				</ButtonsGroup>
				<Button variant="primary-danger" onClick={onDeletePage} isDisabled={isSaving} isLoading={isDeleting}>
					Delete
				</Button>
			</ButtonsRow>
		</>
	)
}

export default InputContent
